// store/store.ts
import { configureStore, Middleware } from '@reduxjs/toolkit';
import { combineReducers, Action } from 'redux';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import logger from 'redux-logger';

import blocksSlice from '../slices/blocks/blocksSlice';
import todaySessionSlice from '../slices/todaySession/todaySessionSlice';
import flashcardsReducer from '../slices/flashcards/flashcardsSlice';
import activeTopicsSlice from '../slices/activeTopics/activeTopicsSlice';
import pastFlashcardsReducer from '../slices/pastFlashcards/pastFlashcardsSlice';
import vignettesReducer from '../slices/vignettes/vignettesSlice';
import statsSlice from '../slices/stats/statsSlice';
import subscriptionsReducer from '../slices/subscriptions/subscriptionsSlice';
import { authApi } from '../services/authApi';
import authReducer from '../slices/auth/authSlice';
import userReducer from '../slices/user/userSlice';
import examsReducer from '../slices/exams/examsSlice';
import lecturesReducer from '../slices/lectures/lecturesSlice';
import pendingLecturesReducer from '../slices/pendingLectures/pendingLecturesSlice';
import schoolReducer from '../slices/school/schoolSlice';
import studySlice from '../slices/study/studySlice';
import todayTopicsSlice from '../slices/todayTopics/todayTopicsSlice';
import videosReducer from '../slices/videos/videosSlice';
import { actionHandlersMiddleware } from '../middleware/actionHandlersMiddleware';

const appReducers = combineReducers({
  activeTopics: activeTopicsSlice,
  blocks: blocksSlice,
  todaySession: todaySessionSlice,
  flashcards: flashcardsReducer,
  pastFlashcards: pastFlashcardsReducer,
  vignettes: vignettesReducer,
  stats: statsSlice,
  subscription: subscriptionsReducer,
  auth: authReducer,
  user: userReducer,
  exams: examsReducer,
  lectures: lecturesReducer,
  pendingLectures: pendingLecturesReducer,
  school: schoolReducer,
  studyPhase: studySlice,
  todayTopics: todayTopicsSlice,
  videos: videosReducer,
  [authApi.reducerPath]: authApi.reducer,
});

// Root reducer to handle global actions like logout
const rootReducer = (
  state: ReturnType<typeof appReducers> | undefined,
  action: Action
): ReturnType<typeof appReducers> => {
  if (action.type === 'auth/logout') {
    storage.removeItem('persist:root');
    state = undefined;
  }
  return appReducers(state, action);
};

const persistConfig = {
  key: 'root',
  storage,
  // blacklist: ['studyPhase'],
  // NOTE: Monitor to see if there is anything we need to blacklist on persist
  // blacklist: [
  //   'activeTopics',
  //   'pastFlashcards',
  //   'stats',
  //   'pendingLectures',
  //   'school',
  //   authApi.reducerPath,
  // ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(authApi.middleware)
      .concat(actionHandlersMiddleware as Middleware);

    if (process.env.NODE_ENV !== 'production') {
      return defaultMiddleware.concat(logger);
    }

    return defaultMiddleware;
  },
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
